import {
  DocumentReference,
  TypeGuard,
  isDocRef,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import {
  IPractice,
  IVerifoneTerminal,
  VerifoneReceiptPrintMethod,
} from '../../practice/practice';

export interface IVerifonePairRequest {
  practiceRef: DocumentReference<IPractice>;
  terminal: Pick<IVerifoneTerminal, 'name' | 'uid'>;
  serialNumber: string;
  testModeEnabled?: boolean;
}

export function isVerifonePairRequest(
  data: unknown
): data is IVerifonePairRequest {
  return TypeGuard.interface<IVerifonePairRequest>({
    practiceRef: isDocRef,
    terminal: TypeGuard.interface<Pick<IVerifoneTerminal, 'name' | 'uid'>>({
      name: isString,
      uid: isString,
    }),
    serialNumber: isString,
    testModeEnabled: TypeGuard.nilOr(isBoolean),
  })(data);
}

export interface IVerifonePairData {
  serial_no: string;
  merchant_id: string;
  register_id: string;
  merchant_name: string;
}

export enum VerifonePairResult {
  Success = 'SUCCESS',
  Failure = 'FAILURE',
  TerminalNotFound = 'TERMINAL_NOT_FOUND',
  TerminalNotAvailable = 'TERMINAL_NOT_AVAILABLE',
  InvalidArgs = 'INVALID_ARGS',
}

export interface IVerifonePairResponse {
  result: VerifonePairResult;
  final_message?: string;
}

export interface IVerifonePurchaseRequest {
  practiceRef: DocumentReference<IPractice>;
  terminal: Pick<IVerifoneTerminal, 'name' | 'uid'>;
  amount: number;
  referenceId?: string;
  testModeEnabled?: boolean;
  receiptPrintMethod?: VerifoneReceiptPrintMethod;
}

export function isVerifonePurchaseRequest(
  data: unknown
): data is IVerifonePurchaseRequest {
  return TypeGuard.interface<IVerifonePurchaseRequest>({
    practiceRef: isDocRef,
    terminal: TypeGuard.interface<Pick<IVerifoneTerminal, 'name' | 'uid'>>({
      name: isString,
      uid: isString,
    }),
    amount: isNumber,
    referenceId: TypeGuard.undefinedOr(isString),
    testModeEnabled: TypeGuard.undefinedOr(isBoolean),
    receiptPrintMethod: TypeGuard.undefinedOr(
      TypeGuard.enumValue(VerifoneReceiptPrintMethod)
    ),
  })(data);
}

export interface IVerifoneRefundRequest {
  practiceRef: DocumentReference<IPractice>;
  terminal: Pick<IVerifoneTerminal, 'name' | 'uid'>;
  amount: number;
  referenceId?: string;
  testModeEnabled?: boolean;
  receiptPrintMethod?: VerifoneReceiptPrintMethod;
}

export function isVerifoneRefundRequest(
  data: unknown
): data is IVerifoneRefundRequest {
  return TypeGuard.interface<IVerifoneRefundRequest>({
    practiceRef: isDocRef,
    terminal: TypeGuard.interface<Pick<IVerifoneTerminal, 'name' | 'uid'>>({
      name: isString,
      uid: isString,
    }),
    amount: isNumber,
    referenceId: TypeGuard.undefinedOr(isString),
    testModeEnabled: TypeGuard.undefinedOr(isBoolean),
    receiptPrintMethod: TypeGuard.undefinedOr(
      TypeGuard.enumValue(VerifoneReceiptPrintMethod)
    ),
  })(data);
}

export interface IVerifonePurchaseData {
  reference_id: string;
  merchant_id: string;
  register_id: string;
  total_amount: number;
  cash_amount?: number;
  pos_prints_receipt?: boolean;
}

export interface IVerifoneRefundData {
  reference_id: string;
  merchant_id: string;
  register_id: string;
  refund_amount: number;
  pos_prints_receipt?: boolean;
}

// enum values intentionally capitalised to match Verifone's API responses

export enum VerifoneTransactionResult {
  Success = 'SUCCESS',
  Failure = 'FAILURE',
  Cancelled = 'CANCELLED',
  Unknown = 'UNKNOWN',
  NoConnection = 'NOCONNECTION',
  Pending = 'PENDING',
}

export enum VerifoneTransactionType {
  Purchase = 'PURCHASE',
  Refund = 'REFUND',
}

export enum VerifoneOnlineStatus {
  Online = 'ONLINE',
  Offline = 'OFFLINE',
}

export interface IVerifoneTransactionResponse {
  txn_type: VerifoneTransactionType;
  reference_id: string;
  result: VerifoneTransactionResult;
  final_message?: string;
  receipt?: string;
  total_amount?: number;
  cash_amount?: number;
  tip_amount?: number;
  surcharge_amount?: number;
  refund_amount?: number;
  online_status?: VerifoneOnlineStatus;
  card_type?: string;
}

export function isVerifoneTransactionResponse(
  data: unknown
): data is IVerifoneTransactionResponse {
  return TypeGuard.interface<IVerifoneTransactionResponse>({
    txn_type: TypeGuard.enumValue(VerifoneTransactionType),
    reference_id: isString,
    result: TypeGuard.enumValue(VerifoneTransactionResult),
    final_message: TypeGuard.nilOr(isString),
    receipt: TypeGuard.nilOr(isString),
    total_amount: TypeGuard.nilOr(isNumber),
    cash_amount: TypeGuard.nilOr(isNumber),
    tip_amount: TypeGuard.nilOr(isNumber),
    surcharge_amount: TypeGuard.nilOr(isNumber),
    refund_amount: TypeGuard.nilOr(isNumber),
    online_status: TypeGuard.nilOr(TypeGuard.enumValue(VerifoneOnlineStatus)),
    card_type: TypeGuard.nilOr(isString),
  })(data);
}

export interface IVerifoneCancelRequest {
  practiceRef: DocumentReference<IPractice>;
  referenceId: string;
  testModeEnabled?: boolean;
}

export function isVerifoneCancelRequest(
  data: unknown
): data is IVerifoneCancelRequest {
  return TypeGuard.interface<IVerifoneCancelRequest>({
    practiceRef: isDocRef,
    referenceId: isString,
    testModeEnabled: TypeGuard.undefinedOr(isBoolean),
  })(data);
}

export interface IVerifonePurchaseTransactionExtendedData {
  request: IVerifonePurchaseRequest;
  response: IVerifoneTransactionResponse;
}

export interface IVerifoneRefundTransactionExtendedData {
  request: IVerifoneRefundRequest;
  response: IVerifoneTransactionResponse;
}

export type IVerifoneTransactionExtendedData =
  | IVerifonePurchaseTransactionExtendedData
  | IVerifoneRefundTransactionExtendedData;

export function isVerifonePurchaseTransactionExtendedData(
  data: unknown
): data is IVerifonePurchaseTransactionExtendedData {
  return TypeGuard.interface<IVerifonePurchaseTransactionExtendedData>({
    request: isVerifonePurchaseRequest,
    response: (value: unknown): value is IVerifoneTransactionResponse =>
      isVerifoneTransactionResponse(value) &&
      value.txn_type === VerifoneTransactionType.Purchase,
  })(data);
}

export function isVerifoneRefundTransactionExtendedData(
  data: unknown
): data is IVerifoneRefundTransactionExtendedData {
  return TypeGuard.interface<IVerifoneRefundTransactionExtendedData>({
    request: isVerifoneRefundRequest,
    response: (value: unknown): value is IVerifoneTransactionResponse =>
      isVerifoneTransactionResponse(value) &&
      value.txn_type === VerifoneTransactionType.Refund,
  })(data);
}

export function isVerifoneTransactionExtendedData(
  data: unknown
): data is IVerifoneTransactionExtendedData {
  return (
    isVerifonePurchaseTransactionExtendedData(data) ||
    isVerifoneRefundTransactionExtendedData(data)
  );
}
